/*
 * Add-to-cart loading state for variant switching.
 *
 * Dawn greys the submit button out for the whole of a variant switch: every option
 * click fires a Section Rendering request, and product-info.js disables the button
 * from resetProductFormState() until the response lands, which base.css paints at 0.5
 * opacity. Measured on this store that window is ~135-200ms warm and 350-570ms on the
 * first switch to a given combination — long enough to read as the button breaking
 * rather than as work in progress.
 *
 * This adds the spinner Dawn already renders inside that button
 * (snippets/buy-buttons.liquid) on top of the grey, driven by the same pub/sub event
 * product-info.js listens to, so assets/product-info.js and assets/product-form.js stay
 * stock Dawn and `git merge dawn/main` stays cheap. Grey plus spinner is the pairing the
 * same button already shows while an add-to-cart is in flight (product-form.js), so the
 * two waits look alike.
 *
 * The button keeps its `disabled` attribute the whole time, and must: the form's hidden
 * id input is not rewritten until the response arrives (product-info.js
 * updateVariantInputs), so an enabled button in that window would add the *previous*
 * variant.
 *
 * Stopping is a MutationObserver on the button rather than the variantChange event,
 * because variantChange does not cover every ending: an unavailable combination makes
 * product-info return from setUnavailable() before it publishes anything. What every
 * ending does share is a write to the button's label — toggleSubmitButton() always
 * sets textContent, and setting it re-creates the text node even when the string is
 * unchanged — so the label is the one signal that catches all of them.
 *
 * A superseded request needs no handling: product-info aborts the older fetch and the
 * newer click re-enters start(), so the spinner simply stays up until the newer
 * response lands. A request that fails outright publishes and renders nothing at all,
 * so the timeout is the only thing that can take the spinner down.
 *
 * The guard is not decoration. Dawn's HTMLUpdateUtility.setInnerHTML re-creates every
 * <script> tag it swaps in, so a combined-listing product swap — which replaces the
 * whole <product-info>, this tag included — runs this file a second time.
 */
if (!window.customVariantLoading) {
  window.customVariantLoading = true;

  /* Only reachable when the request never resolves; a slow cold render is well inside it. */
  const VARIANT_LOADING_TIMEOUT = 10000;

  /* Keyed by button so a quick-add modal's form and the main one cannot clear each other. */
  const inFlight = new WeakMap();

  const stopVariantLoading = (button) => {
    const state = inFlight.get(button);
    if (!state) return;

    inFlight.delete(button);
    state.observer.disconnect();
    clearTimeout(state.timer);

    button.classList.remove('loading');
    button.removeAttribute('aria-busy');
    button.querySelector('.loading__spinner')?.classList.add('hidden');
  };

  const startVariantLoading = (button) => {
    /* The sold-out fallback button in buy-buttons.liquid has no spinner and no form. */
    const spinner = button.querySelector('.loading__spinner');
    if (!spinner || inFlight.has(button)) return;

    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');
    spinner.classList.remove('hidden');

    /* Observed after the mutations above, so unhiding the spinner cannot stop itself. */
    const observer = new MutationObserver(() => stopVariantLoading(button));
    observer.observe(button, { childList: true, characterData: true, subtree: true });

    inFlight.set(button, {
      observer,
      timer: setTimeout(() => stopVariantLoading(button), VARIANT_LOADING_TIMEOUT),
    });
  };

  /*
   * Scoped through the changed input rather than the page: product-info.js keys off
   * `this.contains(event.target)` for the same reason, and a PDP can carry more than one
   * product form once complementary products render quick-add.
   */
  subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
    const button = data?.event?.target?.closest?.('product-info')?.querySelector('.product-form__submit');
    if (button) startVariantLoading(button);
  });
}
